import { MassActionsReducerActionsTypeEnum } from './enums/mass-actions-reducer-actions-type.enum';
import { MassActionsReducerActions } from './types/MassActionsReducerActions.type';
import { MassActionsState } from './useMassActions.interfaces';

export const massActionsInitialState: MassActionsState<never> = {
  selectedItems: new Set(),
};

export const massActionsReducer =
  <T>() =>
  (state: MassActionsState<T>, action: MassActionsReducerActions<T>): MassActionsState<T> => {
    switch (action.type) {
      case MassActionsReducerActionsTypeEnum.CLEAR_SELECTED_ITEMS:
        return massActionsInitialState;
      case MassActionsReducerActionsTypeEnum.TOGGLE_ITEM: {
        const selectedItems = new Set(state.selectedItems);
        const isSelected = selectedItems.has(action.payload);

        isSelected ? selectedItems.delete(action.payload) : selectedItems.add(action.payload);

        return {
          ...state,
          selectedItems,
        };
      }
      case MassActionsReducerActionsTypeEnum.TOGGLE_ITEMS: {
        const selectedItems = new Set(state.selectedItems);

        for (const item of action.payload) {
          if (selectedItems.has(item)) {
            selectedItems.delete(item);
          } else {
            selectedItems.add(item);
          }
        }

        return {
          ...state,
          selectedItems,
        };
      }
      case MassActionsReducerActionsTypeEnum.ADD_ALL_IF_NONE_SELECTED_OR_CLEAR_ALL: {
        if (state.selectedItems.size === 0) {
          return {
            ...state,
            selectedItems: action.payload,
          };
        }

        return massActionsInitialState;
      }
      case MassActionsReducerActionsTypeEnum.SYNC_AND_TOGGLE_ITEMS: {
        const selectedItems = new Set(state.selectedItems);

        const isSubset = Array.from(action.payload).every((item) => selectedItems.has(item));

        if (isSubset) {
          for (const item of action.payload) {
            selectedItems.delete(item);
          }
        } else {
          for (const item of action.payload) {
            selectedItems.add(item);
          }
        }

        return {
          ...state,
          selectedItems,
        };
      }
      case MassActionsReducerActionsTypeEnum.SELECT_ITEM: {
        const selectedItems = new Set(state.selectedItems);

        selectedItems.add(action.payload);

        return {
          ...state,
          selectedItems,
        };
      }
      case MassActionsReducerActionsTypeEnum.SELECT_ITEMS: {
        const selectedItems = new Set(state.selectedItems);

        return {
          ...state,
          selectedItems: selectedItems.union(action.payload),
        };
      }
      case MassActionsReducerActionsTypeEnum.CLEAR_AND_SELECT_ITEMS: {
        return {
          ...state,
          selectedItems: action.payload,
        };
      }
      case MassActionsReducerActionsTypeEnum.REMOVE_ITEM: {
        const selectedItems = new Set(state.selectedItems);

        selectedItems.delete(action.payload);

        return {
          ...state,
          selectedItems,
        };
      }
      case MassActionsReducerActionsTypeEnum.REMOVE_ITEMS: {
        const selectedItems = new Set(state.selectedItems);

        for (const item of action.payload) {
          selectedItems.delete(item);
        }

        return {
          ...state,
          selectedItems,
        };
      }
      case MassActionsReducerActionsTypeEnum.RESET_STATE:
        return massActionsInitialState;
      default:
        return state;
    }
  };

export const clearSelectedItems = <T>(): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.CLEAR_SELECTED_ITEMS,
});
export const toggleItem = <T>(payload: T): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.TOGGLE_ITEM,
  payload,
});
export const toggleItems = <T>(payload: Set<T>): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.TOGGLE_ITEMS,
  payload,
});
export const syncAndToggleItems = <T>(payload: Set<T>): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.SYNC_AND_TOGGLE_ITEMS,
  payload,
});
export const selectItem = <T>(payload: T): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.SELECT_ITEM,
  payload,
});
export const selectItems = <T>(payload: Set<T>): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.SELECT_ITEMS,
  payload,
});
export const clearAndSelectItems = <T>(payload: Set<T>): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.CLEAR_AND_SELECT_ITEMS,
  payload,
});
export const removeItem = <T>(payload: T): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.REMOVE_ITEM,
  payload,
});
export const removeItems = <T>(payload: Set<T>): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.REMOVE_ITEMS,
  payload,
});
export const resetState = <T>(): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.RESET_STATE,
});
export const addAllIfNoneSelectedOrClearAll = <T>(
  payload: Set<T>,
): MassActionsReducerActions<T> => ({
  type: MassActionsReducerActionsTypeEnum.ADD_ALL_IF_NONE_SELECTED_OR_CLEAR_ALL,
  payload,
});
